import { useEffect, useRef } from 'react'
import type { User } from 'firebase/auth'
import { doc } from 'firebase/firestore'
import type { DocumentData, DocumentReference } from 'firebase/firestore'
import { updateDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { useFirestoreDoc } from './useFirestore'

interface ProfileDoc extends DocumentData {
  friendCode?: string
}

function generateFriendCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
}

// Returns the signed-in user's permanent friendCode, generating one on first use.
// Anonymous users get null (they can't own a friend invite link).
export function useFriendCode(user: User | null): { friendCode: string | null; loading: boolean } {
  const isReal = !!user && !user.isAnonymous
  const ref = isReal ? (doc(db, 'users', user.uid) as DocumentReference<ProfileDoc>) : null
  const { data, loading } = useFirestoreDoc<ProfileDoc>(ref)
  const writing = useRef(false)

  useEffect(() => {
    if (!isReal || loading || !data || data.friendCode || writing.current) return
    writing.current = true
    updateDoc(doc(db, 'users', user!.uid), { friendCode: generateFriendCode() }).catch(() => {
      writing.current = false
    })
  }, [isReal, loading, data, user])

  return { friendCode: data?.friendCode ?? null, loading: isReal ? loading : false }
}
